import { useEffect, useRef, useState } from "react";
import {
  ActionIcon, Alert, Badge, Box, Button, Card, Center, Checkbox, CloseButton, Container, Divider, Grid, Group,
  Highlight, Loader, Modal, MultiSelect, Pagination, SegmentedControl, Select, Stack, Text, TextInput, Title, UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconChevronRight, IconFilter, IconFilterOff, IconFolderPlus, IconMapPin, IconSearch } from "@tabler/icons-react";
import { useLocation, useSearch } from "wouter";
import { useTranslation } from "react-i18next";
import { searchCandidatesAdvanced, getCandidateFilterOptions, getProjects, batchAddProjectCandidates } from "../api";
import { CandidateAvatar } from "../Candidates/CandidateAvatar";
import ExpandableTags from "../Candidates/ExpandableTags";
import CandidateHoverCard from "../components/CandidateHoverCard";
import LanguageBadges from "../components/LanguageBadges";
import MatchReasons from "../components/MatchReasons";
import ContactActions from "../components/ContactActions";
import CompanyLink from "../components/CompanyLink";
import { formatCandidateName } from "../_Shared/candidateName";
import { candidateRoleAndCompany } from "../_Shared/candidateRole";
import { candidateLocation } from "../_Shared/candidateLocation";
import GeoLocationAutocomplete from "../components/GeoLocationAutocomplete";
import { fromGeoLocation, geoRequestField, parseGeoFilter, radiusEligible, serializeGeoFilter } from "./geoFilter";
import { usePlaceSuggestion } from "./usePlaceSuggestion";
import { useStore } from "../store";

const suggestedQueries = ["CEO", "CFO", "Strategy", "Operations"];

const radiusPresetsKm = [10, 25, 50, 100, 250];

function parseSearchParams(search) {
  const params = new URLSearchParams(search);
  return {
    q: params.get("q") || "",
    // Legacy free-text location param — still honored for old links / MCP-built URLs, but the
    // UI only ever creates the structured `geo` filter.
    location: params.get("location") || "",
    geo: parseGeoFilter(params.get("geo")),
    skills: params.getAll("skills"),
    languages: params.getAll("languages"),
    tags: params.getAll("tags"),
    similarTo: params.get("similarTo") || "",
    sort: params.get("sort") === "alpha" ? "alpha" : "relevance",
  };
}

function buildSearchParams({ q, location, geo, skills, languages, tags, similarTo, sort }) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (location) params.set("location", location);
  if (geo) params.set("geo", serializeGeoFilter(geo));
  skills.forEach((s) => params.append("skills", s));
  languages.forEach((l) => params.append("languages", l));
  tags.forEach((t) => params.append("tags", t));
  if (similarTo) params.set("similarTo", similarTo);
  if (sort === "alpha") params.set("sort", "alpha");
  const str = params.toString();
  return str ? `?${str}` : "";
}

function hasAnyFilter({ q, location, geo, skills, languages, tags, similarTo }) {
  return q.trim() || location || geo || skills.length || languages.length || tags.length || similarTo;
}

// Multi-select survives navigation (open a candidate, hit back, the selection is still there — like
// Gmail). It's scoped to the exact search it belongs to, and kept in sessionStorage so it survives a
// back-navigation or accidental reload but not a closed tab: this is ephemeral working state, not a
// saved list. `key` is the search's query string; ids are candidate ids.
const SELECTION_STORAGE_KEY = "veft.searchSelection";

function loadPersistedSelection(searchKey) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SELECTION_STORAGE_KEY) || "null");
    if (parsed && parsed.key === searchKey && Array.isArray(parsed.ids)) return new Set(parsed.ids);
  } catch { /* ignore malformed / unavailable storage */ }
  return new Set();
}

function persistSelection(searchKey, ids) {
  try {
    sessionStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify({ key: searchKey, ids: [...ids] }));
  } catch { /* storage unavailable / quota — non-fatal */ }
}

export default function SearchPage() {
  const { t } = useTranslation("Veft");
  const nameFormat = useStore((s) => s.nameFormat);
  const [, setLocation] = useLocation();
  const search = useSearch();

  // Parse URL on mount
  const initial = parseSearchParams(search);
  const initialKey = buildSearchParams(initial);

  const [query, setQuery] = useState(initial.q);
  const [location_, setLocation_] = useState(initial.location);
  // Structured geo filter (compact object, see geoFilter.js) + the text in the location box.
  // The text is display state only — a filter exists exactly when `geoFilter` is set; free
  // typing that doesn't resolve to a pick clears it (no half-filters from unresolved text).
  const [geoFilter, setGeoFilter] = useState(initial.geo);
  const [locationText, setLocationText] = useState(initial.geo?.label || initial.location);
  const [skills, setSkills] = useState(initial.skills);
  const [languages, setLanguages] = useState(initial.languages);
  const [tags, setTags] = useState(initial.tags);
  const [similarTo, setSimilarTo] = useState(initial.similarTo);
  const [sort, setSort] = useState(initial.sort);

  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  // Semantic suggestions arrive from a separate async call (they pay an embedding API
  // round-trip) and render below the lexical results — never blocking them.
  const [semanticResults, setSemanticResults] = useState([]);
  const [semanticLoading, setSemanticLoading] = useState(false);
  // A failed request must render as an ERROR, never as "0 candidates found" — an empty result
  // makes the user (rightly) distrust the search. See docs/Veft/search-guarantees.md G5.
  const [searchError, setSearchError] = useState(false);
  // Filter-only searches (no query, no similar-to) have no relevance signal — the backend
  // returns them alphabetically by last name. Coupled rendering: those results show
  // "Last, First" so the order is visible, overriding the name-format preference.
  const [alphaResults, setAlphaResults] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);

  // Multi-select for bulk "add to project". Selection is scoped to a specific search (see
  // selectionKeyRef) and persisted so a round-trip to a candidate profile and back keeps it; a
  // genuinely new search clears it (see executeSearch / handleClearAll).
  const [selectedIds, setSelectedIds] = useState(() => loadPersistedSelection(initialKey));
  // The search the current selection belongs to. Re-running the same search keeps the selection;
  // running a different one clears it. Seeded from the URL so back-navigation restores correctly.
  const selectionKeyRef = useRef(initialKey);
  const [projects, setProjects] = useState([]);
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [targetProjectId, setTargetProjectId] = useState(null);
  const [addingToProject, setAddingToProject] = useState(false);
  const [showFilters, setShowFilters] = useState(
    () => !!(initial.location || initial.geo || initial.skills.length || initial.languages.length || initial.tags.length)
  );

  // Place-suggestion chip: a free-text query that IS a place name ("Berlin") offers a
  // one-click switch to the precise structured filter. Off while a geo filter is active.
  const { suggestion: placeSuggestion, retrieve: retrievePlace, dismiss: dismissPlace } =
    usePlaceSuggestion(query, { disabled: !!geoFilter });

  const debounceRef = useRef(null);
  // Latest search state, re-assigned every render. Async continuations — the 400 ms query
  // debounce, the geo pick's retrieve resolution, the place-chip apply — MUST build their
  // next state by spreading THIS with only their own delta, never from their closure: the
  // closure is a snapshot from keystroke/pick time, and anything edited while the timer or
  // retrieve was pending would silently roll back (e.g. a geo pick landing inside the
  // debounce window used to fire an unfiltered search while the chip showed the filter).
  const stateRef = useRef(null);
  stateRef.current = { q: query, location: location_, geo: geoFilter, skills, languages, tags, similarTo, sort };
  // Monotonic search sequence — responses from superseded searches (fast typing, page flips)
  // are dropped instead of overwriting newer results.
  const searchSeqRef = useRef(0);
  // Abort superseded in-flight requests instead of letting them run to completion — the
  // backend cancels its (expensive) work when the connection drops.
  const lexicalAbortRef = useRef(null);
  const semanticAbortRef = useRef(null);

  // Load filter options on mount
  useEffect(() => {
    getCandidateFilterOptions()
      .then(setFilterOptions)
      .catch((e) => console.error("Failed to load filter options", e));
  }, []);

  // Auto-search if URL had params on mount
  useEffect(() => {
    if (hasAnyFilter(initial)) {
      executeSearch(initial);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror the selection into sessionStorage so navigating to a candidate and back keeps it.
  useEffect(() => {
    persistSelection(selectionKeyRef.current, selectedIds);
  }, [selectedIds]);

  function updateUrl(state) {
    const qs = buildSearchParams(state);
    window.history.replaceState({}, "", `/search${qs}`);
  }

  async function executeSearch(state, pageArg = 1) {
    const { q, location: loc, geo, skills: sk, languages: lang, tags: tg, similarTo: sim, sort: sortMode } = state || {
      q: query, location: location_, geo: geoFilter, skills, languages, tags, similarTo, sort,
    };

    if (!hasAnyFilter({ q, location: loc, geo, skills: sk, languages: lang, tags: tg, similarTo: sim })) return;

    setLoading(true);
    setHasSearched(true);

    // Clear the selection only when the search actually changed. Re-running the same search — e.g.
    // this page remounting after a back-navigation — keeps whatever was selected. Paging within a
    // search keeps the selection too (the key ignores the page), Gmail-style.
    const selectionKey = buildSearchParams({ q, location: loc, geo, skills: sk, languages: lang, tags: tg, similarTo: sim });
    if (selectionKey !== selectionKeyRef.current) {
      selectionKeyRef.current = selectionKey;
      setSelectedIds(new Set());
    }

    const seq = ++searchSeqRef.current;
    lexicalAbortRef.current?.abort();
    const abort = new AbortController();
    lexicalAbortRef.current = abort;

    // Similar-to stays a single "full" call (one stored-embedding lookup, no query to embed);
    // everything else gets the fast lexical stage — the semantic branch runs separately below.
    const request = { pageSize: 30, page: pageArg, stage: sim ? "full" : "lexical" };
    if (sortMode === "alpha") request.sort = "alpha";
    if (q.trim()) request.query = q.trim();
    // Legacy string filter still travels for old links; the backend ignores it when `geo` is set.
    if (loc) request.location = loc;
    // The semantic branch inherits `geo` via its `...baseRequest` spread below.
    if (geo) request.geo = geoRequestField(geo);
    if (sk.length) request.skills = sk;
    if (lang.length) request.languages = lang;
    if (tg.length) request.tags = tg;
    if (sim) request.similarToCandidateId = sim;

    // Kick off the semantic suggestions in parallel on a NEW query search — not on page flips
    // (the panel is page-independent) and not for filter-only/similar-to searches.
    if (!sim && q.trim() && pageArg === 1) {
      fetchSemantic(request, seq);
    } else if (sim || !q.trim()) {
      semanticAbortRef.current?.abort();
      setSemanticResults([]);
      setSemanticLoading(false);
    }

    try {
      const data = await searchCandidatesAdvanced(request, { signal: abort.signal });
      if (seq !== searchSeqRef.current) return; // superseded by a newer search
      // Each item is { candidate, matches, tier }; flatten to the candidate but keep the match
      // signals on `__matches` so the row can show *why* the contact was found.
      const items = data?.results ?? [];
      setResults(items.map((r) => (r.candidate ? { ...r.candidate, __matches: r.matches ?? [], __tier: r.tier } : r)));
      setTotalCount(data?.totalCount ?? items.length);
      setPage(data?.page ?? pageArg);
      setAlphaResults((!q.trim() && !sim) || sortMode === "alpha");
      setTruncated(!!data?.truncated);
      setSearchError(false);
    } catch (e) {
      if (e?.name === "AbortError" || seq !== searchSeqRef.current) return; // superseded — keep what's shown
      console.error("Search failed", e);
      setResults([]);
      setTotalCount(0);
      setPage(1);
      setTruncated(false);
      setSearchError(true);
    } finally {
      if (seq === searchSeqRef.current) setLoading(false);
    }
  }

  async function fetchSemantic(baseRequest, seq) {
    semanticAbortRef.current?.abort();
    const abort = new AbortController();
    semanticAbortRef.current = abort;

    setSemanticLoading(true);
    setSemanticResults([]);
    try {
      // pageSize covers both suggestion tiers (fuzzy ≤30 + semantic ≤20) in one page.
      const data = await searchCandidatesAdvanced(
        { ...baseRequest, stage: "semantic", page: 1, pageSize: 50 },
        { signal: abort.signal });
      if (seq !== searchSeqRef.current) return;
      const items = data?.results ?? [];
      setSemanticResults(items.map((r) => (r.candidate ? { ...r.candidate, __matches: r.matches ?? [], __tier: r.tier } : r)));
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error("Semantic search failed", e);
      if (seq === searchSeqRef.current) setSemanticResults([]);
    } finally {
      if (seq === searchSeqRef.current) setSemanticLoading(false);
    }
  }

  function handlePageChange(nextPage) {
    setPage(nextPage);
    executeSearch({ q: query, location: location_, geo: geoFilter, skills, languages, tags, similarTo, sort }, nextPage);
    window.scrollTo({ top: 0 });
  }

  function handleSearchNow() {
    const state = { q: query, location: location_, geo: geoFilter, skills, languages, tags, similarTo, sort };
    updateUrl(state);
    executeSearch(state);
  }

  function handleQueryChange(val) {
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // A single character churns the backend for junk results — auto-search needs ≥2 chars;
      // Enter / the search button still fires immediately via handleSearchNow.
      if (val.trim().length === 1) return;
      // stateRef, not closure: a geo pick can land inside the debounce window (see stateRef).
      const state = { ...stateRef.current, q: val };
      updateUrl(state);
      if (hasAnyFilter(state)) executeSearch(state);
    }, 400);
  }

  function handleFilterChange(key, val) {
    const setters = { location: setLocation_, skills: setSkills, languages: setLanguages, tags: setTags };
    setters[key](val);
    const state = { q: query, location: location_, geo: geoFilter, skills, languages, tags, similarTo, sort, [key]: val };
    updateUrl(state);
    if (hasAnyFilter(state)) executeSearch(state);
  }

  // From the location autocomplete. A pick yields a structured filter (radius reset — a new
  // place invalidates the old circle); free typing or clearing yields none and drops whatever
  // filter (structured or legacy ?location=) was active — the box text alone never filters.
  function handleGeoChange({ location: text, geoLocation }) {
    setLocationText(text);
    const gf = geoLocation
      ? fromGeoLocation({ ...geoLocation, displayName: geoLocation.displayName || text })
      : null;
    // Guard reads stateRef (not the closure): a pick resolves asynchronously, so this can run
    // from a stale render whose geoFilter/location_ snapshot predates edits made meanwhile.
    if (!gf && !stateRef.current.geo && !stateRef.current.location) return; // plain typing, no active filter to clear
    // Past the guard the geo state actually changes — a pending query debounce must not fire
    // around it with its pre-change snapshot (the search below already carries the fresh q).
    clearTimeout(debounceRef.current);
    setGeoFilter(gf);
    setLocation_("");
    const state = { ...stateRef.current, location: "", geo: gf };
    updateUrl(state);
    if (hasAnyFilter(state)) executeSearch(state);
  }

  // Chip click: resolve the suggestion to a full GeoLocation and swap the free-text query
  // for the structured filter (radius 0). A failed retrieve is a silent no-op — the chip
  // stays and the click can be retried.
  async function handlePlaceSuggestionApply() {
    const geo = await retrievePlace();
    const gf = geo ? fromGeoLocation(geo) : null;
    if (!gf) return;
    clearTimeout(debounceRef.current); // a pending query debounce must not resurrect the old text search
    setQuery("");
    setGeoFilter(gf);
    setLocationText(gf.label || "");
    setLocation_("");
    setShowFilters(true); // reveal the location box + radius control the filter now lives in
    // stateRef, not closure: skills/languages/tags edited while the retrieve was in flight
    // must survive the swap (the closure is a chip-click-time snapshot).
    const state = { ...stateRef.current, q: "", location: "", geo: gf };
    updateUrl(state);
    executeSearch(state);
  }

  function handleRadiusChange(val) {
    if (!geoFilter) return;
    const gf = { ...geoFilter, radiusKm: Number(val) || 0 };
    setGeoFilter(gf);
    const state = { q: query, location: location_, geo: gf, skills, languages, tags, similarTo, sort };
    updateUrl(state);
    executeSearch(state);
  }

  function handleClearAll() {
    clearTimeout(debounceRef.current); // a pending query debounce must not resurrect the cleared search
    setQuery("");
    setLocation_("");
    setGeoFilter(null);
    setLocationText("");
    setSkills([]);
    setLanguages([]);
    setTags([]);
    setSimilarTo("");
    setSort("relevance");
    lexicalAbortRef.current?.abort();
    semanticAbortRef.current?.abort();
    setResults([]);
    setSemanticResults([]);
    setSemanticLoading(false);
    setSearchError(false);
    setLoading(false);
    setTotalCount(0);
    setPage(1);
    setTruncated(false);
    searchSeqRef.current += 1; // invalidate in-flight responses
    setHasSearched(false);
    selectionKeyRef.current = "";
    setSelectedIds(new Set());
    window.history.replaceState({}, "", "/search");
  }

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAllSelected() {
    setSelectedIds((prev) => {
      const allIds = results.map((c) => c.id);
      return allIds.every((id) => prev.has(id)) ? new Set() : new Set(allIds);
    });
  }

  async function openAddToProject() {
    setAddProjectOpen(true);
    if (projects.length === 0) {
      try {
        const data = await getProjects();
        setProjects(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to load projects", e);
      }
    }
  }

  async function handleAddToProject() {
    if (!targetProjectId || selectedIds.size === 0) return;
    setAddingToProject(true);
    try {
      const res = await batchAddProjectCandidates(targetProjectId, [...selectedIds]);
      const project = projects.find((p) => p.id === targetProjectId);
      notifications.show({
        color: "green",
        title: t("added_to_project"),
        message: t("bulk_added_to_project", {
          added: res.added,
          skipped: res.alreadyOnProject,
          project: project?.title ?? "",
        }),
      });
      setAddProjectOpen(false);
      setTargetProjectId(null);
      setSelectedIds(new Set());
    } catch (e) {
      console.error("Bulk add to project failed", e);
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    } finally {
      setAddingToProject(false);
    }
  }

  function removeFilter(key, value) {
    if (key === "q") {
      handleQueryChange("");
    } else if (key === "geo" || key === "location") {
      handleGeoChange({ location: "", geoLocation: null }); // clears box text + both filter kinds
    } else {
      const current = { skills, languages, tags }[key];
      handleFilterChange(key, current.filter((v) => v !== value));
    }
  }

  function handleSuggestion(q) {
    setQuery(q);
    const state = { q, location: location_, geo: geoFilter, skills, languages, tags, similarTo, sort };
    updateUrl(state);
    executeSearch(state);
  }

  function handleSortChange(next) {
    const nextSort = next === "alpha" ? "alpha" : "relevance";
    setSort(nextSort);
    const state = { ...stateRef.current, sort: nextSort };
    updateUrl(state);
    if (hasAnyFilter(state)) executeSearch(state, 1);
  }

  // Collect active filters for chip display
  const activeFilters = [];
  if (geoFilter) {
    activeFilters.push({
      key: "geo",
      value: geoFilter.label ?? "geo",
      label: geoFilter.radiusKm > 0
        ? t("filter_location_chip_radius", { place: geoFilter.label, km: geoFilter.radiusKm })
        : t("filter_location_chip", { place: geoFilter.label }),
    });
  } else if (location_) {
    activeFilters.push({ key: "location", value: location_, label: t("filter_location_chip", { place: location_ }) });
  }
  skills.forEach((s) => activeFilters.push({ key: "skills", value: s, label: s }));
  languages.forEach((l) => activeFilters.push({ key: "languages", value: l, label: l }));
  tags.forEach((t) => activeFilters.push({ key: "tags", value: t, label: t }));

  const filtersActive = activeFilters.length > 0;

  // Lexical results come from the fast call; semantic suggestions from the async one, appended
  // on page 1 only (the panel is page-independent). The in-list divider marks the boundary.
  const displayResults =
    similarTo || page > 1 ? results : [...results, ...semanticResults];

  return (
    <Container size="md" py="md">
    <Stack gap="lg">
      <Title order={3}>{t("candidate_search")}</Title>

      {/* Search bar */}
      <Card>
        {/* The input keeps its own full width; on a phone the two buttons wrap to a second row
            below it (minWidth on the input forces the wrap) instead of squeezing it to "Kon…". */}
        <Group gap="sm" wrap="wrap" align="center">
          <TextInput
            placeholder={t("search_placeholder")}
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => handleQueryChange(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearchNow()}
            style={{ flex: 1, minWidth: 220 }}
            size="md"
          />
          <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
            <Button
              variant={showFilters ? "light" : "subtle"}
              size="md"
              onClick={() => setShowFilters((v) => !v)}
              leftSection={showFilters ? <IconFilterOff size={16} /> : <IconFilter size={16} />}
            >
              {t("filters")}
            </Button>
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={handleSearchNow}
              size="md"
              loading={loading}
              style={{ flexShrink: 0 }}
            >
              {t("search_button")}
            </Button>
          </Group>
        </Group>

        {/* Filter bar */}
        {showFilters && filterOptions && (
          <Grid mt="md" align="flex-end">
            <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
              <GeoLocationAutocomplete
                label={t("location_label")}
                placeholder={t("any_location")}
                value={locationText}
                onChange={handleGeoChange}
                clearable
                showTypeBadges
                size="sm"
              />
            </Grid.Col>
            {/* Radius applies to a city/address pick only — around a region or country
                centroid a distance circle is meaningless, so the control stays hidden. */}
            {radiusEligible(geoFilter) && (
              <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
                <Select
                  label={t("geo_radius_label")}
                  data={[
                    { value: "0", label: t("geo_radius_exact") },
                    ...radiusPresetsKm.map((km) => ({ value: String(km), label: t("geo_radius_option", { km }) })),
                  ]}
                  value={String(geoFilter.radiusKm || 0)}
                  onChange={handleRadiusChange}
                  allowDeselect={false}
                  size="sm"
                />
              </Grid.Col>
            )}
            <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
              <MultiSelect
                label={t("skills_label")}
                placeholder={t("any_skills")}
                data={filterOptions.skills}
                value={skills}
                onChange={(val) => handleFilterChange("skills", val)}
                searchable
                clearable
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
              <MultiSelect
                label={t("languages_label")}
                placeholder={t("any_language")}
                data={filterOptions.languages}
                value={languages}
                onChange={(val) => handleFilterChange("languages", val)}
                searchable
                clearable
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
              <MultiSelect
                label={t("tags_label")}
                placeholder={t("any_tags")}
                data={filterOptions.tags}
                value={tags}
                onChange={(val) => handleFilterChange("tags", val)}
                searchable
                clearable
                size="sm"
              />
            </Grid.Col>
            {filtersActive && (
              <Grid.Col span="content">
                <Button variant="subtle" size="sm" color="red" onClick={handleClearAll}>
                  {t("clear_all")}
                </Button>
              </Grid.Col>
            )}
          </Grid>
        )}

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <Group gap="xs" mt="sm">
            {activeFilters.map((f) => (
              <Badge
                key={`${f.key}-${f.value}`}
                size="sm"
                variant="light"
                rightSection={
                  <CloseButton size={14} variant="transparent" onClick={() => removeFilter(f.key, f.value)} />
                }
              >
                {f.label}
              </Badge>
            ))}
          </Group>
        )}

        {/* Suggested queries when nothing entered */}
        {!hasSearched && !filtersActive && !query && (
          <Group gap="sm" mt="md">
            <Text size="xs" c="dimmed">{t("try_search")}</Text>
            {suggestedQueries.map((sq) => (
              <Badge
                key={sq}
                size="sm"
                variant="outline"
                style={{ cursor: "pointer" }}
                onClick={() => handleSuggestion(sq)}
              >
                {sq}
              </Badge>
            ))}
          </Group>
        )}
      </Card>

      {/* Similar-to banner */}
      {similarTo && (
        <Card p="sm" bg="blue.0">
          <Group justify="space-between">
            <Text size="sm">{t("similar_banner")}</Text>
            <Button size="xs" variant="subtle" onClick={() => {
              setSimilarTo("");
              const state = { q: query, location: location_, geo: geoFilter, skills, languages, tags, similarTo: "", sort };
              updateUrl(state);
              if (hasAnyFilter(state)) executeSearch(state);
            }}>
              {t("clear")}
            </Button>
          </Group>
        </Card>
      )}

      {/* Big loader only when there's nothing on screen yet — otherwise the previous list
          stays visible (dimmed) while the new request runs, so typing doesn't blank the page
          on every debounce tick. */}
      {loading && results.length === 0 && <Center py="xl"><Loader /></Center>}

      {/* Failed request — explicitly NOT the empty state (G5: an error must never read as
          "0 candidates found"). */}
      {hasSearched && !loading && searchError && (
        <Card p="md" withBorder>
          <Text size="sm" c="red">{t("search_failed")}</Text>
        </Card>
      )}

      {/* Results — kept on screen while a superseding request is in flight (dimmed below). */}
      {hasSearched && !searchError && (results.length > 0 || !loading) && (
        <>
          {/* Place-suggestion chip: the query looks like a place name — offer swapping the
              mention-anywhere text match for the precise location filter. */}
          {placeSuggestion && (
            <Group>
              <Badge
                size="lg"
                variant="outline"
                radius="xl"
                tt="none"
                fw={500}
                style={{ cursor: "pointer" }}
                leftSection={<IconMapPin size={14} />}
                rightSection={
                  <CloseButton
                    size={16}
                    variant="transparent"
                    aria-label={t("search_place_chip_dismiss")}
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissPlace();
                    }}
                  />
                }
                onClick={handlePlaceSuggestionApply}
              >
                {t("search_place_chip", { place: placeSuggestion.name })}
              </Badge>
            </Group>
          )}
          <Group justify="space-between" align="center">
            {results.length > 0 ? (
              <Checkbox
                size="sm"
                checked={results.every((c) => selectedIds.has(c.id))}
                indeterminate={selectedIds.size > 0 && !results.every((c) => selectedIds.has(c.id))}
                onChange={toggleAllSelected}
                label={t("select_all")}
              />
            ) : <span />}
            <Group gap="xs" align="center">
              {loading && <Loader size="xs" />}
              {(query.trim() || similarTo) && (
                <SegmentedControl
                  size="xs"
                  value={sort}
                  onChange={handleSortChange}
                  data={[
                    { value: "relevance", label: t("search_sort_relevance") },
                    { value: "alpha", label: t("sort_alpha") },
                  ]}
                />
              )}
              <Text size="sm" c="dimmed">
                {t("candidates_found", { count: totalCount })}
              </Text>
            </Group>
          </Group>
          {truncated && (
            <Alert color="yellow" variant="light">
              {t("search_truncated")}
            </Alert>
          )}

          {/* Always rendered once there are results, so selecting the first candidate doesn't push
              the list down. Neutral while empty, highlighted (blue) once something is selected. */}
          {results.length > 0 && (
            <Card
              p="xs"
              withBorder
              bg={selectedIds.size > 0 ? "blue.0" : undefined}
              style={{ position: "sticky", top: 0, zIndex: 2 }}
            >
              <Group justify="space-between" wrap="wrap" gap="xs">
                {selectedIds.size > 0 ? (
                  <Text size="sm" fw={500}>{t("selected_count", { count: selectedIds.size })}</Text>
                ) : (
                  <Text size="sm" c="dimmed">{t("select_hint")}</Text>
                )}
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconFolderPlus size={14} />}
                    onClick={openAddToProject}
                    disabled={selectedIds.size === 0}
                  >
                    {t("add_to_project")}
                  </Button>
                  {selectedIds.size > 0 && (
                    <Button size="xs" variant="subtle" color="gray" onClick={() => setSelectedIds(new Set())}>
                      {t("clear_selection")}
                    </Button>
                  )}
                </Group>
              </Group>
            </Card>
          )}

          <Stack gap="sm" style={{ opacity: loading ? 0.55 : 1, transition: "opacity 100ms" }}>
            {displayResults.map((candidate, idx) => {
              // Terms to highlight in name/role — the literal query words (empty array = no-op).
              const highlightTerms = query.trim() ? query.trim().split(/\s+/) : [];
              const { role, company, companyId, isFormer } = candidateRoleAndCompany(candidate);
              const roleLabel = isFormer && role ? `${t("role_former_prefix")} ${role}` : role;
              const location = candidateLocation(candidate);
              const skills = (candidate.skills ?? []).slice(0, 5);
              // Fuzzy (stemmed/close-spelling) and semantic tiers render behind labeled
              // dividers so they read as what they are, not literal matches. In similar-to
              // mode everything is semantic by definition — the banner already explains,
              // so no divider.
              const prevTier = idx === 0 ? undefined : displayResults[idx - 1].__tier;
              const tierBoundary = candidate.__tier !== prevTier && !similarTo;
              const dividerLabel =
                tierBoundary && candidate.__tier === "fuzzy" ? t("search_fuzzy_divider")
                : tierBoundary && candidate.__tier === "semantic" ? t("search_semantic_divider")
                : null;
              return (
                <Box key={candidate.id}>
                {dividerLabel && (
                  <Divider label={dividerLabel} labelPosition="left" mb="sm" />
                )}
                <Card
                  p={0}
                  withBorder
                  style={{
                    cursor: "pointer",
                    overflow: "hidden",
                    borderColor: selectedIds.has(candidate.id) ? "var(--mantine-color-blue-5)" : undefined,
                  }}
                  onClick={() => setLocation(`/candidates/${candidate.id}`)}
                >
                  <Group gap={0} wrap="nowrap" align="stretch">
                    {/* The card body is a link to the candidate, so the checkbox gets its own quiet
                        column, divided off by a hairline — an easy, unambiguous target that never opens
                        the card. The whole column toggles selection (see .search-select-col). */}
                    <UnstyledButton
                      type="button"
                      className="search-select-col"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelected(candidate.id);
                      }}
                      aria-label={t("select")}
                      aria-pressed={selectedIds.has(candidate.id)}
                    >
                      <Checkbox
                        checked={selectedIds.has(candidate.id)}
                        readOnly
                        tabIndex={-1}
                        aria-hidden
                        style={{ pointerEvents: "none" }}
                        mt={4}
                      />
                    </UnstyledButton>
                    <Box p="md" style={{ minWidth: 0, flex: 1 }}>
                      <Group gap="sm" wrap="nowrap" align="flex-start">
                        <CandidateHoverCard candidate={candidate}>
                          <CandidateAvatar candidate={candidate} size="md" radius="xl" color="blue" style={{ flexShrink: 0 }} />
                        </CandidateHoverCard>
                        <Box style={{ minWidth: 0, flex: 1 }}>
                      <Group gap="xs" align="center" wrap="wrap">
                        <Highlight component="span" fw={500} highlight={highlightTerms} style={{ lineHeight: 1.3 }}>
                          {formatCandidateName(candidate, alphaResults ? "lastFirst" : nameFormat)}
                        </Highlight>
                        {candidate.openToWork && (
                          <Badge size="xs" color="teal" variant="light">{t("open_to_work")}</Badge>
                        )}
                      </Group>
                      {/* Role and company are two elements, not one joined string, so the company
                          can be a link to its page. Both keep the query highlight — the company is
                          often the field that matched (a "Ed. Züblin" search), so losing the
                          highlight here would lose the reason the row is in the list. */}
                      {(role || company) && (
                        <Text component="div" size="sm" c="dimmed" lineClamp={2}>
                          {roleLabel && (
                            <Highlight component="span" highlight={highlightTerms} inherit>
                              {roleLabel}
                            </Highlight>
                          )}
                          {roleLabel && company && " · "}
                          <CompanyLink company={company} companyId={companyId} highlight={highlightTerms} />
                        </Text>
                      )}
                      {location && <Text size="xs" c="dimmed">{location}</Text>}
                      <ContactActions contact={candidate} size="xs" mt={4} />

                      {(candidate.languages ?? []).length > 0 && (
                        <Box mt={6}><LanguageBadges languages={candidate.languages} /></Box>
                      )}

                      {(skills.length > 0 || (candidate.tags ?? []).length > 0) && (
                        <Group gap={4} wrap="wrap" mt={6}>
                          {skills.map((s) => (
                            <Badge key={s} size="xs" variant="light" color="blue" tt="none">{s}</Badge>
                          ))}
                          {(candidate.skills?.length ?? 0) > skills.length && (
                            <Badge size="xs" variant="light" color="gray">
                              +{(candidate.skills?.length ?? 0) - skills.length}
                            </Badge>
                          )}
                          {(candidate.tags ?? []).length > 0 && <ExpandableTags tags={candidate.tags} limit={3} />}
                        </Group>
                      )}

                      <MatchReasons matches={candidate.__matches} mt={8} />
                        </Box>
                        <ActionIcon variant="subtle" size="sm" color="gray" style={{ flexShrink: 0 }}>
                          <IconChevronRight size={14} />
                        </ActionIcon>
                      </Group>
                    </Box>
                  </Group>
                </Card>
                </Box>
              );
            })}
          </Stack>

          {/* The semantic call is still in flight — show where its results will appear. */}
          {semanticLoading && page === 1 && !similarTo && (
            <>
              <Divider label={t("search_semantic_divider")} labelPosition="left" />
              <Center py="xs"><Loader size="sm" /></Center>
            </>
          )}

          {totalCount > 30 && (
            <Center mt="sm">
              <Pagination total={Math.ceil(totalCount / 30)} value={page} onChange={handlePageChange} />
            </Center>
          )}
        </>
      )}

      <Modal opened={addProjectOpen} onClose={() => setAddProjectOpen(false)} title={t("add_to_project")}>
        <Stack>
          <Text size="sm" c="dimmed">{t("bulk_add_prompt", { count: selectedIds.size })}</Text>
          <Select
            label={t("project")}
            placeholder={t("select_project")}
            data={projects.map((p) => ({ value: p.id, label: p.title }))}
            value={targetProjectId}
            onChange={setTargetProjectId}
            searchable
            nothingFoundMessage={t("no_projects")}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAddProjectOpen(false)}>{t("cancel")}</Button>
            <Button onClick={handleAddToProject} loading={addingToProject} disabled={!targetProjectId}>
              {t("add")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
    </Container>
  );
}
